import { useState } from 'react';
import { Check, X, AlertTriangle, Package, CreditCard, Award, ChevronDown, Gamepad2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { formatGradingCompany } from '@/constants/gradingCompanies';
import { TRADING_CARD_GAMES, getGameLabel } from '@/constants/tradingCardGames';
import type { EditablePreviewItem, PreviewFilter, PreviewSummary, CollectrPreviewTableProps } from './types';

const CONDITIONS = [
  { value: 'mint', label: 'Mint' },
  { value: 'near_mint', label: 'Near Mint' },
  { value: 'lightly_played', label: 'Lightly Played' },
  { value: 'moderately_played', label: 'Moderately Played' },
  { value: 'heavily_played', label: 'Heavily Played' },
  { value: 'damaged', label: 'Damaged' },
];

const CATEGORIES = [
  { value: 'sealed', label: 'Sealed', icon: Package },
  { value: 'singles', label: 'Single', icon: CreditCard },
  { value: 'slabs', label: 'Slab', icon: Award },
];

function getCategoryIcon(category: string) {
  switch (category) {
    case 'sealed':
      return <Package className="h-4 w-4 text-info" />;
    case 'singles':
      return <CreditCard className="h-4 w-4 text-caution" />;
    case 'slabs':
      return <Award className="h-4 w-4 text-destructive" />;
    default:
      return null;
  }
}

export function CollectrPreviewTable({
  items,
  onItemChange,
  onToggleInclude,
  onBulkSetGame,
  filter,
  onFilterChange,
}: CollectrPreviewTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  // Calculate summary
  const summary: PreviewSummary = {
    total: items.length,
    included: items.filter(i => i._include).length,
    excluded: items.filter(i => !i._include).length,
    needsReview: items.filter(i => i.game === 'other').length,
    sealed: items.filter(i => i.category === 'sealed').length,
    singles: items.filter(i => i.category === 'singles').length,
    slabs: items.filter(i => i.category === 'slabs').length,
  };
  
  // Filter items
  const filteredItems = items.filter(item => {
    switch (filter) {
      case 'needs-review':
        return item.game === 'other';
      case 'excluded':
        return !item._include;
      case 'sealed':
        return item.category === 'sealed';
      case 'singles':
        return item.category === 'singles';
      case 'slabs':
        return item.category === 'slabs';
      default:
        return true;
    }
  });

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      {/* Filter Bar */}
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant={filter === 'all' ? 'default' : 'ghost'}
          onClick={() => onFilterChange('all')}
          className={cn(
            'h-7 text-xs',
            filter !== 'all' && 'backdrop-blur-sm bg-black/20 border border-white/[0.08] hover:border-white/20'
          )}
        >
          All ({summary.total})
        </Button>
        
        {summary.needsReview > 0 && (
          <Button
            size="sm"
            variant={filter === 'needs-review' ? 'default' : 'ghost'}
            onClick={() => onFilterChange('needs-review')}
            className={cn(
              'h-7 text-xs',
              filter === 'needs-review' 
                ? 'bg-warning/20 text-warning border-warning/40 hover:bg-warning/30'
                : 'backdrop-blur-sm bg-warning/10 border border-warning/20 hover:border-warning/40 text-warning'
            )}
          >
            <AlertTriangle className="h-3 w-3 mr-1" />
            Needs Review ({summary.needsReview})
          </Button>
        )}
        
        {summary.excluded > 0 && (
          <Button
            size="sm"
            variant={filter === 'excluded' ? 'default' : 'ghost'}
            onClick={() => onFilterChange('excluded')}
            className={cn(
              'h-7 text-xs',
              filter !== 'excluded' && 'backdrop-blur-sm bg-black/20 border border-white/[0.08] hover:border-white/20 text-muted-foreground'
            )}
          >
            <X className="h-3 w-3 mr-1" />
            Excluded ({summary.excluded})
          </Button>
        )}
        
        <div className="flex-1" />
        
        {/* Bulk action for 'other' games */}
        {summary.needsReview > 0 && (
          <Select onValueChange={(game) => onBulkSetGame(game, 'other')}>
            <SelectTrigger className="h-7 w-auto text-xs gap-1 bg-warning/10 border-warning/20 hover:border-warning/40">
              <Gamepad2 className="h-3 w-3 text-warning" />
              <span>Set all 'Other' to...</span>
            </SelectTrigger>
            <SelectContent className="bg-background/95 backdrop-blur-md border-white/10">
              {TRADING_CARD_GAMES.filter(g => g.value !== 'other').map(game => (
                <SelectItem key={game.value} value={game.value} className="text-xs">
                  {game.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 min-h-0 max-h-[420px] overflow-y-auto overscroll-contain rounded-md border border-white/[0.08] bg-black/20 backdrop-blur-md">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white/[0.04] backdrop-blur-xl supports-[backdrop-filter]:bg-white/[0.04] shadow-[inset_0_-1px_0_0_hsl(var(--border)/0.5)]">
            <TableRow className="border-white/[0.08] hover:bg-transparent">
              <TableHead className="w-8 px-2">
                <Checkbox 
                  checked={items.every(i => i._include)}
                  onCheckedChange={(checked) => {
                    items.forEach(item => {
                      if (item._include !== !!checked) {
                        onToggleInclude(item._id);
                      }
                    });
                  }}
                  className="border-white/30"
                />
              </TableHead>
              <TableHead className="min-w-[200px]">Title</TableHead>
              <TableHead className="w-[140px]">Game</TableHead>
              <TableHead className="w-[100px]">Category</TableHead>
              <TableHead className="w-[120px]">Condition</TableHead>
              <TableHead className="w-[50px] text-center">Qty</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {filteredItems.map((item) => (
              <TableRow 
                key={item._id} 
                className={cn(
                  'border-white/[0.05]',
                  !item._include && 'opacity-50',
                  item.game === 'other' && item._include && 'bg-warning/5'
                )}
              >
                {/* Include checkbox */}
                <TableCell className="px-2">
                  <Checkbox 
                    checked={item._include}
                    onCheckedChange={() => onToggleInclude(item._id)}
                    className="border-white/30"
                  />
                </TableCell>
                
                {/* Title */}
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium truncate max-w-[250px]" title={item.title}>
                      {item.title}
                    </span>
                    {item.set_name && (
                      <span className="text-xs text-muted-foreground truncate max-w-[250px]">
                        {item.set_name}
                        {item.card_number && ` • #${item.card_number}`}
                        {item.slab_grade && ` • ${formatGradingCompany(item.grading_company)} ${item.slab_grade}`}
                      </span>
                    )}
                  </div>
                </TableCell>
                
                {/* Game dropdown */}
                <TableCell>
                  <Select 
                    value={item.game || 'other'} 
                    onValueChange={(val) => onItemChange(item._id, 'game', val)}
                  >
                    <SelectTrigger 
                      className={cn(
                        'h-7 text-xs w-full',
                        item.game === 'other' 
                          ? 'border-warning/40 bg-warning/10 text-warning' 
                          : 'border-white/10 bg-black/20'
                      )}
                    >
                      <SelectValue>
                        <div className="flex items-center gap-1">
                          {item.game === item._originalGame && item.game !== 'other' && (
                            <span className="text-[10px] text-muted-foreground">auto:</span>
                          )}
                          {item.game === 'other' && <AlertTriangle className="h-3 w-3 text-warning" />}
                          <span className="truncate">{getGameLabel(item.game)}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-md border-white/10 max-h-[200px]">
                      {TRADING_CARD_GAMES.map(game => (
                        <SelectItem key={game.value} value={game.value} className="text-xs">
                          {game.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                
                {/* Category dropdown */}
                <TableCell>
                  <Select 
                    value={item.category} 
                    onValueChange={(val) => onItemChange(item._id, 'category', val)}
                  >
                    <SelectTrigger className="h-7 text-xs w-full border-white/10 bg-black/20">
                      <SelectValue>
                        <div className="flex items-center gap-1.5">
                          {getCategoryIcon(item.category)}
                          <span>{CATEGORIES.find(c => c.value === item.category)?.label}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-md border-white/10">
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value} className="text-xs">
                          <div className="flex items-center gap-1.5">
                            {getCategoryIcon(cat.value)}
                            <span>{cat.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                
                {/* Condition dropdown */}
                <TableCell>
                  <Select 
                    value={item.condition} 
                    onValueChange={(val) => onItemChange(item._id, 'condition', val)}
                  >
                    <SelectTrigger className="h-7 text-xs w-full border-white/10 bg-black/20">
                      <SelectValue>
                        {CONDITIONS.find(c => c.value === item.condition)?.label}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-md border-white/10">
                      {CONDITIONS.map(cond => (
                        <SelectItem key={cond.value} value={cond.value} className="text-xs">
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                
                {/* Quantity */}
                <TableCell className="text-center">
                  <Badge variant="outline" className="text-xs font-mono border-white/10">
                    {item.quantity}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                  No items match this filter
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Summary footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>
          {summary.included} of {summary.total} items will be imported
        </span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Package className="h-3 w-3 text-info" />
            {summary.sealed}
          </span>
          <span className="flex items-center gap-1">
            <CreditCard className="h-3 w-3 text-caution" />
            {summary.singles}
          </span>
          <span className="flex items-center gap-1">
            <Award className="h-3 w-3 text-destructive" />
            {summary.slabs}
          </span>
        </div>
      </div>
    </div>
  );
}
